import React, { useState, useEffect, useCallback } from 'react';
import { Gauge, Check, Plus, Trash2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import api from '../utils/api';

const formatVal = (val) => {
  if (val === null || val === undefined) return '—';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '—';
  return Math.round(num).toLocaleString('da-DK');
};

const formatPct = (val) => {
  if (val === null || val === undefined) return '—';
  return `${val.toFixed(1).replace('.', ',')}%`;
};

const DiffBadge = ({ diff, diffPct, unit }) => {
  if (diff === null || diff === undefined) return <span className="text-white/20">—</span>;
  const isPos = diff > 0;
  const isZero = diff === 0;
  const color = isPos ? 'text-green-400' : isZero ? 'text-white/50' : 'text-red-400';
  const bg = isPos ? 'bg-green-400/10' : isZero ? 'bg-white/5' : 'bg-red-400/10';
  const Icon = isPos ? TrendingUp : isZero ? Minus : TrendingDown;
  const diffDisplay = unit === '%' ? `${diff > 0 ? '+' : ''}${diff.toFixed(1).replace('.', ',')}pp` : `${diff > 0 ? '+' : ''}${formatVal(diff)}`;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${color} ${bg}`}>
      <Icon className="w-3 h-3" />
      {diffDisplay} ({diffPct > 0 ? '+' : ''}{diffPct}%)
    </span>
  );
};

const QuarterRow = ({ q, onSave, onDelete, valueKey, testPrefix }) => {
  const [editing, setEditing] = useState(false);
  const expectedVal = q[valueKey];
  const [value, setValue] = useState(expectedVal ?? '');
  const [saving, setSaving] = useState(false);
  const isPercent = valueKey === 'expected_dg';
  const actualKey = valueKey === 'expected_dpt' ? 'actual_dpt' : 'actual_dg';
  const actualVal = q[actualKey];
  const hasActual = actualVal !== null && actualVal !== undefined && actualVal > 0;

  const handleSave = async () => {
    const numVal = parseFloat(value);
    if (isNaN(numVal) || numVal < 0) return;
    setSaving(true);
    await onSave(q.year, q.quarter, numVal);
    setSaving(false);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') { setEditing(false); setValue(expectedVal ?? ''); }
  };

  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`${testPrefix}-row-${q.year}-${q.quarter}`}>
      <td className="px-4 py-3">
        <span className="text-white font-medium text-sm">{q.quarter_label}</span>
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex items-center justify-end gap-2">
            <input
              data-testid={`${testPrefix}-input-${q.year}-${q.quarter}`}
              type="number"
              step={isPercent ? '0.1' : '1'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              className="w-24 bg-white/10 border border-[#1E6F7A]/50 rounded px-2 py-1 text-white text-sm text-right focus:outline-none focus:border-[#1E6F7A] tabular-nums"
            />
            {isPercent && <span className="text-white/40 text-sm">%</span>}
            <button
              data-testid={`${testPrefix}-save-${q.year}-${q.quarter}`}
              onClick={handleSave}
              disabled={saving}
              className="p-1 rounded bg-[#1E6F7A]/20 hover:bg-[#1E6F7A]/40 text-[#1E6F7A] transition-colors"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            data-testid={`${testPrefix}-edit-${q.year}-${q.quarter}`}
            onClick={() => setEditing(true)}
            className="text-sm tabular-nums hover:text-[#1E6F7A] transition-colors cursor-pointer text-right w-full"
          >
            {expectedVal !== null && expectedVal !== undefined ? (
              <span className="text-white/90">{isPercent ? formatPct(expectedVal) : formatVal(expectedVal)}</span>
            ) : (
              <span className="text-white/20 italic">Klik for at sætte</span>
            )}
          </button>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <span className={`text-sm tabular-nums ${hasActual ? 'text-[#1E6F7A]' : 'text-white/20'}`}>
          {hasActual ? (isPercent ? formatPct(actualVal) : formatVal(actualVal)) : '—'}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <DiffBadge diff={q.diff} diffPct={q.diff_pct} unit={isPercent ? '%' : ''} />
      </td>
      <td className="px-4 py-3 text-center text-white/30 text-xs tabular-nums">
        {q.weeks_with_data > 0 ? `${q.weeks_with_data} uger` : '—'}
      </td>
      <td className="px-4 py-3 text-center">
        {expectedVal !== null && expectedVal !== undefined && (
          <button
            data-testid={`${testPrefix}-delete-${q.year}-${q.quarter}`}
            onClick={() => onDelete(q.year, q.quarter)}
            className="p-1 rounded hover:bg-red-400/10 text-white/20 hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
};

const CockpitTab = ({ tab, quarters, loading, onSave, onDelete, onAdd }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [addYear, setAddYear] = useState(new Date().getFullYear());
  const [addQuarter, setAddQuarter] = useState(1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 1 + i);

  const isDG = tab === 'dg';
  const label = isDG ? 'DG' : 'DPT';
  const fullLabel = isDG ? 'Dækningsgrad' : 'Dækning pr. time';
  const valueKey = isDG ? 'expected_dg' : 'expected_dpt';
  const testPrefix = isDG ? 'dg' : 'dpt';
  const actualLabel = isDG ? 'Faktisk DG' : 'Faktisk DPT';
  const expectedLabel = isDG ? 'Forventet DG' : 'Forventet DPT';

  const handleAdd = async () => {
    const exists = quarters.some(q => q.year === addYear && q.quarter === addQuarter);
    if (exists) { setShowAdd(false); return; }
    await onAdd(addYear, addQuarter);
    setShowAdd(false);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Forventet {label} pr. kvartal</h2>
          <p className="text-white/40 text-sm mt-1">Sammenlign forventet {label} med faktisk beregnet {label}</p>
        </div>
        <button
          data-testid={`add-${testPrefix}-quarter-btn`}
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E6F7A]/20 hover:bg-[#1E6F7A]/30 text-[#1E6F7A] rounded text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Tilføj kvartal
        </button>
      </div>

      {showAdd && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4 flex items-end gap-4" data-testid={`add-${testPrefix}-form`}>
          <div>
            <label className="block text-white/50 text-xs mb-1">År</label>
            <select value={addYear} onChange={(e) => setAddYear(parseInt(e.target.value))}
              className="bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#1E6F7A]">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Kvartal</label>
            <select value={addQuarter} onChange={(e) => setAddQuarter(parseInt(e.target.value))}
              className="bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#1E6F7A]">
              {[1,2,3,4].map(q => <option key={q} value={q}>Q{q}</option>)}
            </select>
          </div>
          <button onClick={handleAdd}
            className="px-4 py-1.5 bg-[#1E6F7A] hover:bg-[#1E6F7A]/80 text-white rounded text-sm font-medium transition-colors">
            Tilføj
          </button>
          <button onClick={() => setShowAdd(false)}
            className="px-4 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 rounded text-sm transition-colors">
            Annuller
          </button>
        </div>
      )}

      <div className="bg-[#111318] border border-white/10 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#1E6F7A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full" data-testid={`${testPrefix}-table`}>
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">Kvartal</th>
                <th className="text-right text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">{expectedLabel}</th>
                <th className="text-right text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">{actualLabel}</th>
                <th className="text-right text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">Afvigelse</th>
                <th className="text-center text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">Data</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {quarters.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-white/30 text-sm">
                    Ingen kvartaler tilføjet endnu. Klik "Tilføj kvartal" for at starte.
                  </td>
                </tr>
              ) : (
                quarters.map((q) => (
                  <QuarterRow
                    key={`${q.year}-${q.quarter}`}
                    q={q}
                    onSave={onSave}
                    onDelete={onDelete}
                    valueKey={valueKey}
                    testPrefix={testPrefix}
                  />
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 flex items-center gap-6 text-xs text-white/30">
        <span className="flex items-center gap-1.5">
          <TrendingUp className="w-3 h-3 text-green-400" />
          Faktisk over forventet
        </span>
        <span className="flex items-center gap-1.5">
          <TrendingDown className="w-3 h-3 text-red-400" />
          Faktisk under forventet
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#1E6F7A]" />
          {label} = {fullLabel}
        </span>
      </div>
    </>
  );
};

export default function BudgetCockpit() {
  const [activeTab, setActiveTab] = useState('dpt');
  const [dptQuarters, setDptQuarters] = useState([]);
  const [dgQuarters, setDgQuarters] = useState([]);
  const [loadingDpt, setLoadingDpt] = useState(true);
  const [loadingDg, setLoadingDg] = useState(true);

  const fetchDpt = useCallback(async () => {
    try {
      const res = await api.get('/budget-cockpit/dpt');
      setDptQuarters(res.data.quarters || []);
    } catch (err) { console.error('Failed to fetch DPT', err); }
    finally { setLoadingDpt(false); }
  }, []);

  const fetchDg = useCallback(async () => {
    try {
      const res = await api.get('/budget-cockpit/dg');
      setDgQuarters(res.data.quarters || []);
    } catch (err) { console.error('Failed to fetch DG', err); }
    finally { setLoadingDg(false); }
  }, []);

  useEffect(() => { fetchDpt(); fetchDg(); }, [fetchDpt, fetchDg]);

  const handleSaveDpt = async (year, quarter, val) => {
    await api.put('/budget-cockpit/dpt', { year, quarter, expected_dpt: val });
    await fetchDpt();
  };
  const handleDeleteDpt = async (year, quarter) => {
    await api.put('/budget-cockpit/dpt', { year, quarter, expected_dpt: null });
    await fetchDpt();
  };
  const handleAddDpt = async (year, quarter) => {
    await handleSaveDpt(year, quarter, 0);
  };

  const handleSaveDg = async (year, quarter, val) => {
    await api.put('/budget-cockpit/dg', { year, quarter, expected_dg: val });
    await fetchDg();
  };
  const handleDeleteDg = async (year, quarter) => {
    await api.put('/budget-cockpit/dg', { year, quarter, expected_dg: null });
    await fetchDg();
  };
  const handleAddDg = async (year, quarter) => {
    await handleSaveDg(year, quarter, 0);
  };

  const tabs = [
    { id: 'dpt', label: 'DPT' },
    { id: 'dg', label: 'DG' },
  ];

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="budget-cockpit-page">
      <header className="bg-[#1A1A1A] border-b border-white/10 px-6 py-4">
        <div className="flex items-center gap-4">
          <Navigation />
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <Gauge className="w-5 h-5 text-[#1E6F7A]" />
            Budget — Cockpit
          </h1>
        </div>
      </header>

      <div className="p-4 md:p-6 max-w-5xl mx-auto">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white/5 rounded-lg p-1 w-fit" data-testid="cockpit-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#1E6F7A] text-white shadow-sm'
                  : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'dpt' && (
          <CockpitTab
            tab="dpt"
            quarters={dptQuarters}
            loading={loadingDpt}
            onSave={handleSaveDpt}
            onDelete={handleDeleteDpt}
            onAdd={handleAddDpt}
          />
        )}
        {activeTab === 'dg' && (
          <CockpitTab
            tab="dg"
            quarters={dgQuarters}
            loading={loadingDg}
            onSave={handleSaveDg}
            onDelete={handleDeleteDg}
            onAdd={handleAddDg}
          />
        )}
      </div>
    </div>
  );
}
